import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { Sparkles, RefreshCw, ClipboardCopy, Check } from "lucide-react";

/**
 * <AiHelper> — pulsante riutilizzabile con dialog per generare testi via AI.
 *
 * Props:
 *  - preset:    string   → uno dei preset backend (coupon_copy, product_copy, …)
 *  - contextLabel: string → label mostrata all'utente (es. "Genera testo coupon")
 *  - hint:      string   → placeholder del prompt
 *  - onApply:   (json_or_text) => void  → callback quando l'utente accetta il risultato
 *  - defaultPrompt: string → prefill del prompt
 *  - triggerVariant: "outline" | "ghost" | "solid"
 *  - triggerLabel: string
 *  - testid:    string
 */
export default function AiHelper({
  preset,
  contextLabel = "Genera con AI",
  hint = "Descrivi in una riga cosa vuoi (es. Offerta di benvenuto -10%)",
  onApply,
  defaultPrompt = "",
  triggerVariant = "outline",
  triggerLabel = "AI",
  testid = "ai-helper",
}) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.6-terra");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [copied, setCopied] = useState(false);

  const providerModels = {
    openai:    ["gpt-5.6-terra", "gpt-5.6-luna", "gpt-5.6-sol", "gpt-5.6"],
    anthropic: ["claude-sonnet-5-5", "claude-sonnet-5", "claude-opus-5-5", "claude-haiku-4-5-20251001"],
    gemini:    ["gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-3.5-flash", "gemini-2.5-pro"],
  };

  const run = async () => {
    if (!prompt.trim()) return toast.error("Scrivi un prompt");
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/ai/text", { provider, model, prompt, preset });
      setResult(data.text || "");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const tryParseJson = (txt) => {
    if (!txt) return null;
    // il modello a volte restituisce testo con code-fence ```json ... ```
    const m = txt.match(/```(?:json)?\s*([\s\S]*?)```/);
    const raw = (m ? m[1] : txt).trim();
    try { return JSON.parse(raw); } catch (_) { return null; }
  };

  const apply = () => {
    if (!result) return;
    const parsed = tryParseJson(result);
    if (onApply) onApply(parsed || result);
    setOpen(false);
    setResult("");
    setPrompt(defaultPrompt);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const variantCls = triggerVariant === "solid"
    ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
    : triggerVariant === "ghost"
      ? "text-fuchsia-300 hover:bg-fuchsia-500/10"
      : "border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10";

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={triggerVariant === "solid" ? "default" : triggerVariant}
        className={variantCls}
        onClick={() => setOpen(true)}
        data-testid={testid}
      >
        <Sparkles className="h-3.5 w-3.5 mr-1" /> {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-xl" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-fuchsia-300" /> {contextLabel}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-400 text-xs mb-1 inline-block">Provider</Label>
                <Select value={provider} onValueChange={(v) => { setProvider(v); setModel(providerModels[v][0]); }}>
                  <SelectTrigger data-testid={`${testid}-provider`} className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI (GPT-5.6)</SelectItem>
                    <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    <SelectItem value="gemini">Google (Gemini)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1 inline-block">Modello</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid={`${testid}-model`} className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providerModels[provider].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Prompt</Label>
              <Textarea data-testid={`${testid}-prompt`}
                value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder={hint} rows={3}
                className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="flex gap-2">
              <Button data-testid={`${testid}-run`} onClick={run} disabled={loading}
                className="bg-fuchsia-600 hover:bg-fuchsia-700">
                {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Genera
              </Button>
            </div>
            {result && (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Risultato</Label>
                <pre data-testid={`${testid}-result`}
                  className="bg-zinc-950 border border-zinc-800 rounded-md p-3 text-xs whitespace-pre-wrap max-h-[40vh] overflow-y-auto">
                  {result}
                </pre>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={copyToClipboard}
                    data-testid={`${testid}-copy`} className="border-zinc-800">
                    {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <ClipboardCopy className="h-3.5 w-3.5 mr-1" />}
                    {copied ? "Copiato" : "Copia"}
                  </Button>
                  {onApply && (
                    <Button data-testid={`${testid}-apply`} size="sm" onClick={apply}
                      className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="h-3.5 w-3.5 mr-1" /> Applica
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
